import { useState, useEffect, useCallback, useMemo } from 'react'
import { useRouter } from '@/routing/navigation'
import { usersApi, organizationsApi } from '@/lib/api'
import type { UserProfile, Organization, OrganizationType } from '@/lib/api'
import { toast as notify } from 'sonner'
import { getUserInitials, getAvatarPaletteForSeed, seedToPaletteIndex, AVATAR_PALETTE_COUNT } from '@/lib/utils/avatar'
import { canCustomizeOwnAvatarColor } from '@/lib/billing/tier'
import { useAuth } from '@/lib/config/auth-provider'

function mergeProfilePreservingAvatarSeed(
  previous: UserProfile | null,
  incoming: Partial<UserProfile> | null | undefined,
): UserProfile | null {
  if (!previous && !incoming) return null

  const merged = {
    ...(previous || {}),
    ...(incoming || {}),
  } as UserProfile

  const hasIncomingAvatarSeed =
    !!incoming && Object.prototype.hasOwnProperty.call(incoming, 'avatarSeed')

  if (!hasIncomingAvatarSeed && previous?.avatarSeed !== undefined) {
    merged.avatarSeed = previous.avatarSeed
  }

  return merged
}

export function useSettingsProfile() {
  const router = useRouter()
  const { refreshProfile: refreshAuthProfile } = useAuth()

  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [profileLoading, setProfileLoading] = useState(true)

  // Account state
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editName, setEditName] = useState('')
  const [saving, setSaving] = useState(false)

  // Organization state
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [editOrgType, setEditOrgType] = useState<OrganizationType | ''>('')

  // Avatar — use persisted seed from profile, or derive from name/email
  const avatarSeed = profile?.avatarSeed || profile?.name || profile?.email || 'user'
  const canCustomizeAvatarColor = canCustomizeOwnAvatarColor(profile?.subscriptionTier)
  const avatarPalette = useMemo(() => getAvatarPaletteForSeed(avatarSeed), [avatarSeed])
  const avatarAppearanceProps = canCustomizeAvatarColor
    ? { appearance: 'palette' as const, bgColor: avatarPalette.bg, fgColor: avatarPalette.fg }
    : { appearance: 'themeTinted' as const }

  const initials = useMemo(
    () => getUserInitials(profile?.name, profile?.email),
    [profile?.name, profile?.email],
  )

  const syncProfileToLocalStorage = useCallback((nextProfile: UserProfile) => {
    try {
      const rawUser = localStorage.getItem('user')
      if (!rawUser) return
      const parsed = JSON.parse(rawUser)
      localStorage.setItem('user', JSON.stringify({ ...parsed, ...nextProfile }))
    } catch (error) {
      console.error('Failed to sync profile to localStorage:', error)
    }
  }, [])

  const refreshProfile = useCallback(async (preloaded?: UserProfile) => {
    const latest = preloaded ?? await usersApi.getProfile()
    const mergedProfile = mergeProfilePreservingAvatarSeed(profile, latest)

    if (mergedProfile) {
      setProfile(mergedProfile)
      syncProfileToLocalStorage(mergedProfile)
    }

    await refreshAuthProfile()

    return mergedProfile
  }, [profile, syncProfileToLocalStorage, refreshAuthProfile])

  // Regenerate avatar with random seed and persist to backend
  const handleRegenerateAvatar = async () => {
    if (!profile || !canCustomizeAvatarColor) return

    const currentProfile = profile
    const currentIndex = seedToPaletteIndex(avatarSeed)
    const nextIndex = (currentIndex + 1) % AVATAR_PALETTE_COUNT
    const randomSeed = `color-${nextIndex}-${Date.now()}`
    const optimisticProfile = mergeProfilePreservingAvatarSeed(currentProfile, {
      avatarSeed: randomSeed,
    })

    if (optimisticProfile) {
      setProfile(optimisticProfile)
      syncProfileToLocalStorage(optimisticProfile)
    }

    try {
      const updated = await usersApi.updateProfile({ avatarSeed: randomSeed })
      const hasAvatarSeedInResponse = Object.prototype.hasOwnProperty.call(
        updated,
        'avatarSeed',
      )
      const returnedAvatarSeed = hasAvatarSeedInResponse
        ? updated.avatarSeed
        : undefined

      if (returnedAvatarSeed !== randomSeed) {
        console.warn(
          'PUT /users/me did not persist requested avatarSeed; using client-side avatar seed fallback.',
        )
      }

      const mergedProfile = mergeProfilePreservingAvatarSeed(
        currentProfile,
        {
          ...updated,
          avatarSeed: returnedAvatarSeed === randomSeed ? returnedAvatarSeed : randomSeed,
        },
      )

      if (mergedProfile) {
        setProfile(mergedProfile)
        syncProfileToLocalStorage(mergedProfile)
      }

      // Refresh auth context so global UI (sidebar, menus) picks up latest profile.
      await refreshAuthProfile()
    } catch (error) {
      setProfile(currentProfile)
      syncProfileToLocalStorage(currentProfile)
      console.error('Failed to update avatar:', error)
      notify.error('Failed to update avatar')
    }
  }

  const handleSaveProfile = async () => {
    setSaving(true)
    try {
      const updated = await usersApi.updateProfile({ name: editName })
      await refreshProfile(updated)

      if (organization && editOrgType && editOrgType !== organization.type) {
        const updatedOrg = await organizationsApi.update(organization.id, {
          type: editOrgType,
        })
        setOrganization({ ...organization, ...updatedOrg })
      }

      setShowEditDialog(false)
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to save changes'
      notify.error(msg)
      console.error('Failed to update profile:', err)
    } finally {
      setSaving(false)
    }
  }

  // Profile initialization
  useEffect(() => {
    let isMounted = true

    const initialize = async () => {
      const user = localStorage.getItem('user')
      if (!user) {
        router.push('/login')
        return
      }

      let localProfile: UserProfile | null = null

      try {
        const rawUser = localStorage.getItem('user')
        if (rawUser && isMounted) {
          const parsed = JSON.parse(rawUser) as UserProfile
          localProfile = parsed
          setProfile(parsed)
        }
      } catch (error) {
        console.error('Failed to parse local profile:', error)
      }

      try {
        const [latest, org] = await Promise.all([
          usersApi.getProfile(),
          organizationsApi.getCurrent().catch(() => null),
        ])
        const mergedProfile = mergeProfilePreservingAvatarSeed(
          localProfile,
          latest,
        )

        if (isMounted) {
          if (mergedProfile) {
            setProfile(mergedProfile)
            syncProfileToLocalStorage(mergedProfile)
          }
          if (org) {
            setOrganization(org)
          }
        }
      } catch (error) {
        console.error('Failed to load profile:', error)
      } finally {
        if (isMounted) {
          setProfileLoading(false)
        }
      }
    }

    initialize()
    return () => {
      isMounted = false
    }
  }, [router, syncProfileToLocalStorage])

  useEffect(() => {
    if (profile?.name) {
      setEditName(profile.name)
    }
  }, [profile?.name])

  useEffect(() => {
    if (organization?.type) {
      setEditOrgType(organization.type)
    }
  }, [organization?.type])

  return {
    profile,
    profileLoading,
    showEditDialog,
    setShowEditDialog,
    editName,
    setEditName,
    saving,
    initials,
    canCustomizeAvatarColor,
    avatarAppearanceProps,
    refreshProfile,
    handleRegenerateAvatar,
    handleSaveProfile,
    organization,
    editOrgType,
    setEditOrgType,
  }
}
